import styled from 'styled-components';

export const ContentSection = styled.div`
  background: var(--white-main);
  border-radius: var(--semi-medium-border-radius);
  padding: var(--default-padding);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 1px 0;

  &:hover {
    opacity: 0.8;
  }
`;

export const ContentHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 37px;
`;

export const ContentIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: var(--semi-big-font-size);
    color: var(--black-main);
  }
`;

export const ContentTitle = styled.div`
  font-family: var(--font-family);
  font-weight: 700;
  font-size: var(--medium-font-size);
  color: var(--black-main);
  line-height: 1.5;
`;

export const ModificationBadge = styled.div`
  background: var(--blue-light);
  border-radius: var(--grand-border-radius);
  padding: var(--xxsmall-padding) 11px;
  font-family: var(--font-family);
  font-weight: 500;
  font-size: var(--xxsmall-font-size);
  color: var(--blue-text);
  line-height: 1.5;
  white-space: nowrap;
`;

export const ChevronIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.3s ease;

  &.expanded {
    transform: rotate(180deg);
  }

  i {
    font-size: var(--xsmall-font-size);
    color: var(--black-main);
  }
`;

export const SlideItemContainer = styled.div`
  background: var(--beige-main);
  border-radius: var(--box-border-radius);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const SlideHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

export const SlideHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
`;

export const SlideTitle = styled.div`
  font-family: var(--font-family);
  font-weight: 600;
  font-size: var(--small-font-size);
  color: var(--black-main);
  line-height: 1.5;
`;

export const ComparisonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ComparisonBox = styled.div<{ type: 'original' | 'customized' }>`
  background: ${(props) =>
    props.type === 'original' ? 'var(--red-pale)' : 'var(--green-pale)'};
  border-radius: var(--box-border-radius);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: var(--font-family);
  font-weight: 500;
  font-size: var(--small-font-size);
  line-height: 1.5;
  flex: 1 0 0;
  min-height: 0;
  min-width: 0;
`;

export const ComparisonLabel = styled.div<{ type: 'original' | 'customized' }>`
  color: ${(props) =>
    props.type === 'original' ? 'var(--red-main)' : 'var(--green-main)'};
  font-weight: 500;
  font-size: var(--small-font-size);
  line-height: 1.5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--xsmall-padding);
`;

export const LabelText = styled.span`
  flex: 1;
`;

export const HeaderButtonContainer = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

export const ComparisonContent = styled.div`
  color: var(--black-main);
  font-weight: 500;
  font-size: var(--small-font-size);
  line-height: 1.5;
  word-break: break-word;
  white-space: pre-wrap;

  &.html-content {
    .ql-editor {
      padding: 0;
      font-size: var(--small-font-size);
      line-height: 1.5;
      color: inherit;

      p {
        margin: 0 0 var(--xsmall-padding) 0;

        &:last-child {
          margin-bottom: 0;
        }
      }

      strong,
      b {
        font-weight: 600;
      }

      ul,
      ol {
        margin: var(--xsmall-padding) 0;
        padding-left: var(--medium-border-radius);
      }

      li {
        margin: var(--xxsmall-padding) 0;
      }
    }
  }

  mark {
    background: var(--green-highlight-bg);
    padding: 0;
  }
`;

export const AnswerOption = styled.div<{ isCorrect?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: var(--xsmall-padding);
  padding: var(--xsmall-padding);
  margin-bottom: var(--xxsmall-padding);
  background: ${(props) =>
    props.isCorrect ? 'var(--green-success-bg)' : 'transparent'};
  border-radius: var(--xxxsmall-border-radius);
  position: relative;

  &::before {
    content: '';
    display: ${(props) => (props.isCorrect ? 'block' : 'none')};
    width: 3px;
    height: 100%;
    background: var(--green-main);
    position: absolute;
    left: 0;
    top: 0;
    border-radius: var(--xxxsmall-border-radius) 0 0
      var(--xxxsmall-border-radius);
  }

  .answer-content {
    flex: 1;
    word-break: break-word;
    padding-left: ${(props) =>
      props.isCorrect ? 'var(--xsmall-padding)' : '0'};
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px var(--default-padding);
  color: var(--gray-darker);
`;
